import { useEffect, useState, useCallback, useRef } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { productTypeValues, type components } from "@dolfin/api-public";
import { enumOptions } from "@/lib/enum-options";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { PermissionGuard } from "@/components/PermissionGuard";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Spinner } from "@/components/ui/spinner";
import { MoreHorizontal, Package, Pencil, Plus, Search, Trash2, Upload, X } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatCurrency, formatDate } from "@/lib/format";
import { CreateProductDialog } from "@/components/CreateProductDialog";
import { BulkUploadProductsDialog } from "@/components/BulkUploadProductsDialog";
import { EditProductDialog } from "@/components/EditProductDialog";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { EmptyState, ErrorState, HeaderRow, LoadingState, Pager, TH, TableShell, rowLink } from "@/components/PageUi";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type ProductResponse = components["schemas"]["ProductResponse"];

// Derived from the OpenAPI enum so new product types appear automatically.
const TYPES = enumOptions(productTypeValues, { all: true });

const PAGE_SIZE = 10;

export interface ProductsListFilters {
  name?: string;
  type?: string;
}

export interface ProductsListPageProps {
  onProductClick: (id: string) => void;
  /** Initial filters. Updates to filters live in component state. */
  defaultFilters?: ProductsListFilters;
  onFiltersChange?: (filters: ProductsListFilters) => void;
}

export function ProductsListPage({
  onProductClick,
  defaultFilters,
  onFiltersChange,
}: ProductsListPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();

  const [products, setProducts] = useState<ProductResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [nameInput, setNameInput] = useState(defaultFilters?.name ?? "");
  const debouncedName = useDebouncedValue(nameInput);
  const [typeFilter, setTypeFilter] = useState(defaultFilters?.type ?? "All");

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [productToDelete, setProductToDelete] = useState<ProductResponse | null>(null);
  const [productToEdit, setProductToEdit] = useState<ProductResponse | null>(null);

  const canManage = can("manageProducts");

  // Ref so a fresh inline callback doesn't re-fire the effect (an emit loop via e.g. setSearchParams).
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  const firstEmit = useRef(true);

  useEffect(() => {
    if (firstEmit.current) {
      // Host already passed these values as defaultFilters; nothing to sync.
      firstEmit.current = false;
      return;
    }
    onFiltersChangeRef.current?.({
      name: debouncedName || undefined,
      type: typeFilter === "All" ? undefined : typeFilter,
    });
  }, [debouncedName, typeFilter]);

  const fetchProducts = useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);

    try {
      const query: Record<string, string | number> = { Limit: PAGE_SIZE };
      if (debouncedName) query.Name = debouncedName;
      if (typeFilter !== "All") query.Type = typeFilter;
      if (cursor) query.Cursor = cursor;

      const { data, error: apiError } = await api.GET("/v1/products", {
        params: { query },
      });

      if (apiError || !data) {
        console.error("Failed to load products:", apiError);
        setError("Failed to load products");
        return;
      }

      setProducts(data.data ?? []);
      const next = data.meta?.cursors?.next ?? null;
      setNextCursor(next);
      setHasMore(next !== null);
    } catch (e) {
      console.error("Failed to load products:", e);
      setError("Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [api, debouncedName, typeFilter]);

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchProducts();
  }, [fetchProducts]);

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchProducts(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchProducts(prevCursor);
  }

  async function confirmDelete() {
    const target = productToDelete;
    if (!target?.id) return;
    const id = target.id;
    setProductToDelete(null);
    setDeletingId(id);
    const { error: apiError } = await api.DELETE("/v1/products/{id}", {
      params: { path: { id } },
    });
    setDeletingId(null);

    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to delete product"));
      return;
    }

    toast.success("Product deleted");
    setProducts((prev) => prev.filter((p) => p.id !== id));
  }

  const currentPage = cursorStack.length + 1;

  const filtered = !!debouncedName || typeFilter !== "All";

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Receivables"
        title="Products"
        subtitle="What you sell, priced once and reused on every invoice."
        actions={
          <>
            <PermissionGuard allowed={canManage} reason="Only owners and admins can add products" className="w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={() => setBulkOpen(true)}
                className="w-full gap-1.5 sm:w-auto"
              >
                <Upload className="size-4" />
                Bulk upload
              </Button>
            </PermissionGuard>
            <PermissionGuard allowed={canManage} reason="Only owners and admins can add products" className="w-full sm:w-auto">
              <Button onClick={() => setCreateOpen(true)} className="w-full gap-1.5 sm:w-auto">
                <Plus className="size-4" />
                New product
              </Button>
            </PermissionGuard>
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            className="h-9 pl-8"
            placeholder="Search by name"
            aria-label="Filter by name"
          />
        </div>
        <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value)}>
          <SelectTrigger className="h-9 w-full sm:w-[140px]" aria-label="Filter by type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            {TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filtered ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setNameInput("");
              setTypeFilter("All");
            }}
          >
            <X className="size-4" /> Clear
          </Button>
        ) : null}
      </div>

      {loading ? (
        <LoadingState label="Loading products..." />
      ) : error ? (
        <ErrorState message="Couldn't load your products." onRetry={() => fetchProducts(cursorStack[cursorStack.length - 1])} />
      ) : products.length === 0 ? (
        filtered ? (
          <EmptyState icon={Search} title="No products match">
            Try a different name or type, or clear the filters.
          </EmptyState>
        ) : (
          <EmptyState
            icon={Package}
            title="No products yet"
            action={
              <div className="flex gap-2">
                <PermissionGuard allowed={canManage} reason="Only owners and admins can add products">
                  <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)}>
                    <Upload className="size-4" /> Bulk upload
                  </Button>
                </PermissionGuard>
                <PermissionGuard allowed={canManage} reason="Only owners and admins can add products">
                  <Button size="sm" onClick={() => setCreateOpen(true)}>
                    <Plus className="size-4" /> New product
                  </Button>
                </PermissionGuard>
              </div>
            }
          >
            Add the goods and services you sell, or upload a spreadsheet of them.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Product</TableHead>
                  <TableHead className={cn(TH, "w-[110px]")}>Type</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Price</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Added</TableHead>
                  <TableHead className="w-[48px]" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {products.map((product) => {
                  const isDeleting = deletingId === product.id;
                  const link = rowLink(product.id ? () => onProductClick(product.id!) : undefined);
                  return (
                    <TableRow key={product.id} {...link}>
                      <TableCell className="max-w-[420px]">
                        <p className="truncate font-semibold">{product.name}</p>
                        <p className="truncate text-xs text-muted-foreground">
                          {product.description || "No description"}
                        </p>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{product.type}</TableCell>
                      <TableCell className="text-right font-semibold tabular-nums">
                        {formatCurrency(product.unitPrice, product.currency)}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                        {formatDate(product.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={isDeleting}
                              className="size-8"
                              aria-label={`Actions for ${product.name ?? "product"}`}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              {isDeleting ? (
                                <Spinner className="size-4" />
                              ) : (
                                <MoreHorizontal className="size-4" />
                              )}
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-40"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DropdownMenuItem onClick={() => setProductToEdit(product)}>
                              <Pencil className="mr-2 size-4" />
                              Edit
                            </DropdownMenuItem>
                            {canManage && (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setProductToDelete(product)}
                              >
                                <Trash2 className="mr-2 size-4" />
                                Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={currentPage}
            hasPrevious={currentPage > 1}
            hasNext={hasMore}
            onPrevious={handlePrevPage}
            onNext={handleNextPage}
          />
        </>
      )}

      <CreateProductDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => {
          setCursorStack([]);
          setNextCursor(null);
          fetchProducts();
        }}
      />

      <BulkUploadProductsDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onUploaded={() => {
          setCursorStack([]);
          setNextCursor(null);
          fetchProducts();
        }}
      />

      <EditProductDialog
        product={productToEdit}
        open={productToEdit !== null}
        onOpenChange={(open) => !open && setProductToEdit(null)}
        onUpdated={() => {
          setProductToEdit(null);
          fetchProducts();
        }}
      />

      <AlertDialog
        open={productToDelete !== null}
        onOpenChange={(open) => !open && setProductToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete product?</AlertDialogTitle>
            <AlertDialogDescription>
              {productToDelete?.name} will be permanently removed. This will fail if the product is referenced by any invoice line item.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
